import {
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import Big from "big.js";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCase } from "../../context/CaseContext";
import { STANDARD_VAT_RATE } from "../../shared/vat";

/**
 * Whether a party can reclaim the VAT on its costs and, when it cannot, the
 * rate it pays. A party that reclaims it has no rate: VAT is left out of its
 * figures. One that cannot starts at the standard rate. The rate is saved
 * when the field is left, not at every key, since every save recalculates
 * the case.
 */
export const PartyVat = ({
  name,
  vatRate,
  onChange,
}: {
  name: string;
  vatRate: number | undefined;
  onChange: (vatRate: number | undefined) => void;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "case.budget.vat" });
  const { readOnly } = useCase();
  // the rate while it is typed, in percent
  const [typed, setTyped] = useState<string>();
  const reclaims = vatRate === undefined;

  const commit = () => {
    if (typed === undefined) return;
    setTyped(undefined);
    const percent = Number.parseFloat(typed);
    if (Number.isNaN(percent) || percent < 0 || percent > 100) return;
    const rate = new Big(percent).div(100).toNumber();
    if (rate !== vatRate) onChange(rate);
  };

  return (
    <Stack spacing={1} sx={{ maxWidth: "72ch" }}>
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        sx={{ alignItems: "center", flexWrap: "wrap" }}
      >
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={reclaims}
              disabled={readOnly}
              onChange={(_, checked) =>
                onChange(checked ? undefined : STANDARD_VAT_RATE)
              }
            />
          }
          label={t("reclaims", { name })}
          slotProps={{ typography: { variant: "body2" } }}
        />
        {!reclaims && (
          <TextField
            size="small"
            type="number"
            label={t("rate")}
            disabled={readOnly}
            value={typed ?? new Big(vatRate).mul(100).toNumber()}
            onChange={({ target }) => setTyped(target.value)}
            onBlur={commit}
            onKeyDown={({ key }) => key === "Enter" && commit()}
            sx={{ width: 140 }}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              },
              htmlInput: { min: 0, max: 100, step: 0.5 },
            }}
          />
        )}
      </Stack>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {t(reclaims ? "reclaimsHelp" : "paysHelp")}
      </Typography>
    </Stack>
  );
};
